import React from 'react';
import { makeStyles } from '@mui/styles';
import { browserName, browserVersion } from "react-device-detect";
import SignalWifi3BarIcon from '@mui/icons-material/SignalWifi3Bar';
import BuildIcon from '@mui/icons-material/Build';
import LaptopWindowsIcon from '@mui/icons-material/LaptopWindows';
import LanguageIcon from '@mui/icons-material/Language';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import detectFlash from 'detect-flash';
import MultiOrg from '../components/MultiOrg';

// Function to measure download speed
const measureDownloadSpeed = async () => {
  const fileSizeInBytes = 100000; // 100KB test
  const testUrl = `https://speed.cloudflare.com/__down?bytes=${fileSizeInBytes}`;

  try {
    const startTime = performance.now();
    const response = await fetch(testUrl, {
      cache: 'no-store',
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    await response.blob();
    const endTime = performance.now();

    const durationInSeconds = (endTime - startTime) / 1000;
    const bitsLoaded = fileSizeInBytes * 8;
    const speedBps = bitsLoaded / durationInSeconds;
    const speedMbps = (speedBps / (1024 * 1024)).toFixed(2);

    return parseFloat(speedMbps);
  } catch (error) {
    console.error('Speed test failed:', error);
    if (navigator.connection && navigator.connection.downlink) {
      return navigator.connection.downlink;
    }
    return null;
  }
};

const useStyles = makeStyles(theme => ({
  content: {
    flexGrow: 80,
    padding: theme.spacing(3),
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    '@media (max-width: 600px)': {
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    '@media (max-width: 600px)': {
      minHeight: '120px',
    },
  },
  cardsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
    marginTop: "16px",
    '@media (max-width: 768px)': {
      gridTemplateColumns: "1fr",
      gap: "12px",
    },
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    padding: "20px",
    boxSizing: "border-box",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
    border: "1px solid #f0f0f0",
    transition: "all 0.2s ease",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
      transform: "translateY(-2px)",
    },
    '@media (max-width: 600px)': {
      padding: "14px",
      width: "100%",
    },
  },
  cardAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
  },
  cardAccentPassed: {
    background: "linear-gradient(90deg, #4caf50 0%, #81c784 100%)",
  },
  cardAccentFailed: {
    background: "linear-gradient(90deg, #f44336 0%, #e57373 100%)",
  },
  cardAccentTesting: {
    background: "linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },
  iconContainer: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
  },
  iconContainerPassed: {
    backgroundColor: "rgba(76, 175, 80, 0.1)",
    color: "#4caf50",
  },
  iconContainerFailed: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#f44336",
  },
  iconContainerTesting: {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
    color: "#ff9800",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#333",
    margin: 0,
  },
  cardBody: {
    marginBottom: "0",
  },
  infoRow: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "12px",
    fontSize: "13px",
    color: "#666",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  infoLabel: {
    color: "#999",
    marginRight: "8px",
    minWidth: "80px",
    flexShrink: 0,
  },
  infoValue: {
    color: "#444",
    fontWeight: 500,
    flex: 1,
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
  },
  statusPassed: {
    backgroundColor: "rgba(76, 175, 80, 0.1)",
    color: "#2e7d32",
  },
  statusFailed: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#c62828",
  },
  statusTesting: {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
    color: "#e65100",
  },
  statusIcon: {
    fontSize: "16px",
    marginRight: "4px",
  },
  remarkText: {
    fontSize: "12px",
    color: "#666",
    marginTop: "12px",
    padding: "10px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    borderLeft: "3px solid",
  },
  remarkPassed: {
    borderLeftColor: "#4caf50",
  },
  remarkFailed: {
    borderLeftColor: "#f44336",
  },
  remarkTesting: {
    borderLeftColor: "#ff9800",
  },
}));

const SiteRequirement = (props) => {
  const classes = useStyles();
  const [internetSpeed, setInternetSpeed] = React.useState(null);
  const [speedTesting, setSpeedTesting] = React.useState(true);
  const [osVersion, setOsversion] = React.useState('');
  const { typeButton, handleHome } = props;
  const [_flashFlag, setFlashFlag] = React.useState(false);

  React.useEffect(() => {
    const testSpeed = async () => {
      setSpeedTesting(true);
      try {
        const speed = await measureDownloadSpeed();
        setInternetSpeed(speed);
      } catch (error) {
        console.error('Speed test error:', error);
        setInternetSpeed(null);
      } finally {
        setSpeedTesting(false);
      }
    };
    testSpeed();

    function checkOsName() {
      var OSName = "Unknown";
      if (window.navigator.userAgent.indexOf("Windows NT 10.0") !== -1) OSName = "Windows 10";
      if (window.navigator.userAgent.indexOf("Windows NT 11.0") !== -1) OSName = "Windows 11";
      if (window.navigator.userAgent.indexOf("Windows NT 6.3") !== -1) OSName = "Windows 8.1";
      if (window.navigator.userAgent.indexOf("Windows NT 6.2") !== -1) OSName = "Windows 8";
      if (window.navigator.userAgent.indexOf("Windows NT 6.1") !== -1) OSName = "Windows 7";
      if (window.navigator.userAgent.indexOf("Windows NT 6.0") !== -1) OSName = "Windows Vista";
      if (window.navigator.userAgent.indexOf("Windows NT 5.1") !== -1) OSName = "Windows XP";
      if (window.navigator.userAgent.indexOf("Windows NT 5.0") !== -1) OSName = "Windows 2000";
      if (window.navigator.userAgent.indexOf("Mac") !== -1) OSName = "Mac/iOS";
      if (window.navigator.userAgent.indexOf("X11") !== -1) OSName = "UNIX";
      if (window.navigator.userAgent.indexOf("Linux") !== -1) OSName = "Linux";
      setOsversion(OSName);
    }
    checkOsName();
  }, []);

  React.useEffect(() => {
    detectFlash('path-to-swf/FlashDetector.swf')
      .then(
        () => setFlashFlag(true),
        () => setFlashFlag(false)
      );
  }, []);

  const bandwidthPassed = !speedTesting && internetSpeed !== null && internetSpeed > 0.3;
  const _bandwidthFailed = !speedTesting && (internetSpeed === null || internetSpeed <= 0.3);
  const osPassed = ["Windows 11", "Windows 10", "Mac/iOS", "Linux", "UNIX"].includes(osVersion);
  const browserPassed = (browserName === 'Chrome' && browserVersion >= 129) ||
    (browserName === 'Firefox' && browserVersion >= 131) ||
    (browserName === 'Edge' && browserVersion >= 129) ||
    (browserName === 'Safari' && browserVersion >= 16) ||
    (browserName === 'Opera' && browserVersion >= 110);

  const _getStatusClass = (passed, testing = false) => {
    if (testing) return 'Testing';
    return passed ? 'Passed' : 'Failed';
  };

  return (
    <main className={classes.content}>
      <div className={classes.toolbar} />
      <div>
        <MultiOrg handleHome={handleHome} typeButton={typeButton} />
      </div>

      <div className={classes.cardsContainer}>
        {/* Bandwidth Card */}
        <div className={classes.card}>
          <div className={`${classes.cardAccent} ${speedTesting ? classes.cardAccentTesting : bandwidthPassed ? classes.cardAccentPassed : classes.cardAccentFailed}`}></div>
          <div className={classes.cardHeader}>
            <div className={`${classes.iconContainer} ${speedTesting ? classes.iconContainerTesting : bandwidthPassed ? classes.iconContainerPassed : classes.iconContainerFailed}`}>
              <SignalWifi3BarIcon />
            </div>
            <h3 className={classes.cardTitle}>Bandwidth</h3>
          </div>
          <div className={classes.cardBody}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Required:</span>
              <span className={classes.infoValue}>0.3 Mbps (Minimum)</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status:</span>
              <span className={`${classes.statusBadge} ${speedTesting ? classes.statusTesting : bandwidthPassed ? classes.statusPassed : classes.statusFailed}`}>
                {speedTesting ? (
                  <><HourglassEmptyIcon className={classes.statusIcon} /> Testing...</>
                ) : bandwidthPassed ? (
                  <><CheckCircleIcon className={classes.statusIcon} /> Passed ({internetSpeed} Mbps)</>
                ) : (
                  <><CancelIcon className={classes.statusIcon} /> {internetSpeed === null ? 'Unable to test' : `Failed (${internetSpeed} Mbps)`}</>
                )}
              </span>
            </div>
          </div>
          <div className={`${classes.remarkText} ${speedTesting ? classes.remarkTesting : bandwidthPassed ? classes.remarkPassed : classes.remarkFailed}`}>
            {speedTesting ? 'Measuring bandwidth...' : bandwidthPassed ? 'Bandwidth is good to support online test' : 'Bandwidth is not sufficient. Please check your connection.'}
          </div>
        </div>

        {/* JavaScript Card */}
        <div className={classes.card}>
          <div className={`${classes.cardAccent} ${classes.cardAccentPassed}`}></div>
          <div className={classes.cardHeader}>
            <div className={`${classes.iconContainer} ${classes.iconContainerPassed}`}>
              <BuildIcon />
            </div>
            <h3 className={classes.cardTitle}>JavaScript</h3>
          </div>
          <div className={classes.cardBody}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Required:</span>
              <span className={classes.infoValue}>Enabled</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status:</span>
              <span className={`${classes.statusBadge} ${classes.statusPassed}`}>
                <CheckCircleIcon className={classes.statusIcon} /> Passed
              </span>
            </div>
          </div>
          <div className={`${classes.remarkText} ${classes.remarkPassed}`}>
            JavaScript is enabled and supported
          </div>
        </div>

        {/* Operating System Card */}
        <div className={classes.card}>
          <div className={`${classes.cardAccent} ${osPassed ? classes.cardAccentPassed : classes.cardAccentFailed}`}></div>
          <div className={classes.cardHeader}>
            <div className={`${classes.iconContainer} ${osPassed ? classes.iconContainerPassed : classes.iconContainerFailed}`}>
              <LaptopWindowsIcon />
            </div>
            <h3 className={classes.cardTitle}>Operating System</h3>
          </div>
          <div className={classes.cardBody}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Required:</span>
              <span className={classes.infoValue}>Windows 10/11, macOS, Linux</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Detected:</span>
              <span className={classes.infoValue}>{osVersion}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status:</span>
              <span className={`${classes.statusBadge} ${osPassed ? classes.statusPassed : classes.statusFailed}`}>
                {osPassed ? (
                  <><CheckCircleIcon className={classes.statusIcon} /> Passed</>
                ) : (
                  <><CancelIcon className={classes.statusIcon} /> Failed</>
                )}
              </span>
            </div>
          </div>
          <div className={`${classes.remarkText} ${osPassed ? classes.remarkPassed : classes.remarkFailed}`}>
            {osPassed ? 'Operating system is supported' : 'Please upgrade to Windows 10 or later'}
          </div>
        </div>

        {/* Browser Card */}
        <div className={classes.card}>
          <div className={`${classes.cardAccent} ${browserPassed ? classes.cardAccentPassed : classes.cardAccentFailed}`}></div>
          <div className={classes.cardHeader}>
            <div className={`${classes.iconContainer} ${browserPassed ? classes.iconContainerPassed : classes.iconContainerFailed}`}>
              <LanguageIcon />
            </div>
            <h3 className={classes.cardTitle}>Browser Support</h3>
          </div>
          <div className={classes.cardBody}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Required:</span>
              <span className={classes.infoValue}>Chrome 129+, Firefox 131+, Edge 129+, Safari 16+</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Detected:</span>
              <span className={classes.infoValue}>{browserName} {browserVersion}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status:</span>
              <span className={`${classes.statusBadge} ${browserPassed ? classes.statusPassed : classes.statusFailed}`}>
                {browserPassed ? (
                  <><CheckCircleIcon className={classes.statusIcon} /> Passed</>
                ) : (
                  <><CancelIcon className={classes.statusIcon} /> Failed</>
                )}
              </span>
            </div>
          </div>
          <div className={`${classes.remarkText} ${browserPassed ? classes.remarkPassed : classes.remarkFailed}`}>
            {browserPassed ? 'Browser is supported' : 'Please update your browser to the latest version'}
          </div>
        </div>
      </div>
    </main>
  );
}

export default SiteRequirement;
